/**
 * AiWorkOrderModal — создание наряда из свободного текстового промпта (Magic Prompt).
 *
 * Workflow (P1 Enterprise Hardening):
 * 1. Инженер пишет задачу в свободной форме
 * 2. AI анализирует контекст ЦОДа и генерирует WorkOrderItems
 * 3. Backend прогоняет физическую валидацию (SpatialEngine + ConnectivityEngine)
 * 4. Инженер резолвит неточные совпадения inline (стойка/каталог/порт)
 * 5. Human Review Gate: список операций + конфликты + чекбокс «Ознакомлен»
 * 6. Только после подтверждения — POST /work-orders
 */
import { useState, useEffect, useRef } from 'react'
import { Modal } from '../ui/Modal'
import { Button } from '../ui/Button'
import { api } from '../../utils/api'
import { Sparkles, Loader2, AlertTriangle, CheckCircle2, RefreshCw, Clock, ShieldAlert, Eye } from 'lucide-react'
import toast from 'react-hot-toast'
import type { WorkOrderPreview, WorkOrderItemPreview } from '../../types/workOrder'
import { is503, getErrorDetail } from '../../utils/aiError'
import { useAuthStore } from '../../stores/authStore'
import { canManageDc } from '../../stores/authRoles.logic'
import { AiErrorBlock } from './AiErrorBlock'
import { ReviewGate } from '../ReviewGate'
import { AiPreviewItemCard, type RackOption, type CatalogOption } from './AiPreviewItemCard'
import { SerialVerificationBlock } from './SerialVerificationBlock'
import { RackDiffPreview } from './RackDiffPreview'
import { buildReviewOperations, collectConflicts, isPhysicallyBlocked, isAdvisoryOnly, canOpenReviewGate, physicsChanged, aiGenerateRequestBody } from './aiPreview.logic'
import { tokenProgressFromSse } from './generationProgress.logic'
import { ruPlural } from '../woLifecycle.logic'
import { rackLabel } from '../../utils/rackName.logic'

interface AiWorkOrderModalProps {
  isOpen: boolean
  onClose: () => void
  dcCode: string
  onCreated: () => void
}

// Examples must not name equipment or racks that this DC does not have —
// the engineer copies them, and the model then has nothing to bind to.
const EXAMPLE_PROMPTS = [
  'Подключи новый сервер к коммутатору ToR',
  'Освободи стойку под новое оборудование',
]

export default function AiWorkOrderModal({
  isOpen,
  onClose,
  dcCode,
  onCreated,
}: AiWorkOrderModalProps) {
  const [prompt, setPrompt] = useState('')
  const [autonomyLevel, setAutonomyLevel] = useState(2)
  const [preview, setPreview] = useState<WorkOrderPreview | null>(null)
  const [loading, setLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  const [elapsed, setElapsed] = useState(0)
  const [aiError, setAiError] = useState<string | null>(null)
  const [showReviewGate, setShowReviewGate] = useState(false)
  // P4-4: ручное создание при недоступном AI (503) — prefilled из промпта
  const [manualMode, setManualMode] = useState(false)
  const [manualTitle, setManualTitle] = useState('')
  const [racks, setRacks] = useState<RackOption[]>([])
  const [catalog, setCatalog] = useState<CatalogOption[]>([])
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  // P2-4: AbortController для отмены генерации; P2-3: ключ идемпотентности POST
  const abortRef = useRef<AbortController | null>(null)
  const idemKeyRef = useRef<string>('')
  // P4-5: реальный прогресс токенов из SSE (null → time-based fallback)
  const [tokenProgress, setTokenProgress] = useState<{ done: number; total: number } | null>(null)
  const userRole = useAuthStore(s => s.userRole)
  const canCommit = canManageDc(userRole)
  const recheckSeq = useRef(0)
  const [recheckPending, setRecheckPending] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      abortRef.current?.abort()
      setAiError(null); setPreview(null); setShowReviewGate(false); setManualMode(false); setRecheckPending(false)
    }
  }, [isOpen])

  useEffect(() => {
    if (loading) {
      setElapsed(0)
      timerRef.current = setInterval(() => setElapsed(s => s + 1), 1000)
    } else {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [loading])

  // Справочники: нужны и для inline-резолва в превью, и для примеров промптов,
  // поэтому грузим при открытии модала, а не при появлении превью.
  useEffect(() => {
    if (!isOpen) return
    if (racks.length === 0) {
      api.get(`/api/datacenters/${dcCode}/racks`)
        .then(res => setRacks(
          (res.data || []).map((r: any) => ({ id: r.id, label: rackLabel(r) }))
        ))
        .catch(() => {})
    }
    if (catalog.length === 0) {
      api.get('/api/equipment/catalog', { params: { limit: 200 } })
        .then(res => setCatalog(
          (res.data || []).map((c: any) => ({
            id: c.id,
            label: `${c.manufacturer} ${c.model} (${c.u_height}U)`,
            u_height: c.u_height,
          }))
        ))
        .catch(() => {})
    }
  }, [isOpen, dcCode])

  // Example built from this DC's real catalog and racks; static fallback while loading.
  const installExample = catalog[0] && racks[0]
    ? `Установи ${catalog[0].label.replace(/\s*\(\d+U\)$/, '')} в стойку ${racks[0].label}`
    : null
  const examplePrompts = installExample ? [installExample, ...EXAMPLE_PROMPTS] : EXAMPLE_PROMPTS

  /** P4-5: слушает SSE прогресса генерации (fetch — EventSource не умеет Authorization). */
  const listenProgress = async (genId: string, signal: AbortSignal) => {
    try {
      const token = useAuthStore.getState().token
      const res = await fetch(
        `/api/datacenters/${dcCode}/impact-analysis/work-orders/ai-generate/progress/${genId}`,
        { signal, headers: token ? { Authorization: `Bearer ${token}` } : {} }
      )
      if (!res.ok || !res.body) return
      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buf = ''
      for (;;) {
        const { value, done } = await reader.read()
        if (done) break
        buf += decoder.decode(value, { stream: true })
        const events = buf.split('\n\n')
        buf = events.pop() ?? ''
        for (const ev of events) {
          const line = ev.split('\n').find(l => l.startsWith('data: '))
          if (!line) continue
          const data = JSON.parse(line.slice(6))
          const next = tokenProgressFromSse(data)
          if (next) setTokenProgress(next)
          if (data.done) return
        }
      }
    } catch {
      // прогресс — best effort; при ошибке остаётся time-based бар
    }
  }

  const handleGenerate = async () => {
    if (!prompt.trim() || prompt.trim().length < 5) {
      toast.error('Опишите задачу подробнее (минимум 5 символов)')
      return
    }
    setLoading(true)
    setPreview(null)
    setAiError(null)
    setTokenProgress(null)
    const controller = new AbortController()
    abortRef.current = controller
    const genId = crypto.randomUUID()
    listenProgress(genId, controller.signal)
    try {
      const res = await api.post(
        `/api/datacenters/${dcCode}/impact-analysis/work-orders/ai-generate`,
        aiGenerateRequestBody(prompt.trim(), autonomyLevel),
        { signal: controller.signal, headers: { 'X-Generation-Id': genId } }
      )
      setRecheckPending(false)
      setPreview(res.data)
    } catch (err: any) {
      if (err.name === 'CanceledError' || err.name === 'AbortError') {
        // Отменено инженером — молча возвращаемся к форме
      } else if (is503(err)) {
        setAiError(getErrorDetail(err))
      } else {
        toast.error('Ошибка AI: ' + getErrorDetail(err))
      }
    } finally {
      setLoading(false)
    }
  }

  const handleCancelGenerate = () => {
    abortRef.current?.abort()
    setLoading(false)
  }

  const handleItemChange = async (index: number, updated: WorkOrderItemPreview) => {
    if (!preview) return
    const prev = preview.items[index]
    const items = preview.items.map((it, i) => (i === index ? updated : it))
    if (!physicsChanged(prev, updated)) {
      setPreview({ ...preview, items })
      return
    }
    setRecheckPending(true)
    setPreview({ ...preview, items })
    const seq = ++recheckSeq.current
    try {
      const res = await api.post(
        `/api/datacenters/${dcCode}/impact-analysis/work-orders/validate-preview`,
        { items },
      )
      if (seq !== recheckSeq.current) return
      setRecheckPending(false)
      setPreview(p => (p ? {
        ...p,
        items: res.data.items,
        commit_block_reason: res.data.commit_block_reason ?? null,
      } : p))
    } catch {
      // fail-closed: leave recheckPending so Create stays off until a later successful recheck
    }
  }

  /** Реальный POST — вызывается только из ReviewGate.onConfirm. */
  const handleCreate = async () => {
    if (!preview) return
    setCreating(true)
    try {
      await api.post(
        `/api/datacenters/${dcCode}/impact-analysis/work-orders`,
        {
          title: preview.title,
          description: preview.description || prompt,
          datacenter_code: dcCode,
          jira_ticket_id: null,
          items: preview.items,
        },
        { headers: { 'X-Idempotency-Key': idemKeyRef.current } }
      )
      toast.success('Наряд создан!')
      setShowReviewGate(false)
      onCreated()
      onClose()
      setPrompt('')
      setPreview(null)
    } catch (err: any) {
      toast.error('Ошибка создания: ' + getErrorDetail(err))
    } finally {
      setCreating(false)
    }
  }

  /** P4-4: наряд без AI — один CUSTOM item с текстом исходного промпта. */
  const handleManualCreate = async () => {
    if (!manualTitle.trim()) {
      toast.error('Укажите заголовок наряда')
      return
    }
    setCreating(true)
    try {
      await api.post(
        `/api/datacenters/${dcCode}/impact-analysis/work-orders`,
        {
          title: manualTitle.trim(),
          description: prompt,
          datacenter_code: dcCode,
          jira_ticket_id: null,
          items: [{ item_type: 'CUSTOM', description: prompt, metadata: {} }],
        },
        { headers: { 'X-Idempotency-Key': crypto.randomUUID() } }
      )
      toast.success('Наряд создан вручную')
      onCreated()
      onClose()
      setPrompt(''); setManualMode(false)
    } catch (err: any) {
      toast.error('Ошибка создания: ' + getErrorDetail(err))
    } finally {
      setCreating(false)
    }
  }

  const unresolvedCount = preview?.items.filter(i => i.has_unresolved).length || 0
  const blockedCount = preview?.items.filter(isPhysicallyBlocked).length || 0
  const advisoryOnly = isAdvisoryOnly(autonomyLevel)
  const conflicts = preview ? collectConflicts(preview.items) : []
  const reviewReady = preview ? canOpenReviewGate(preview.commit_block_reason) : false

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Создать наряд через AI" size="xl">
      {/* P4-4: ручная форма при недоступном AI — prefilled промптом */}
      {manualMode && !preview && (
        <div className="space-y-4" data-testid="manual-create-form">
          <p className="text-sm text-text-secondary">
            AI недоступен — наряд будет создан вручную с текстом вашей задачи.
          </p>
          <div>
            <label className="block text-sm font-semibold text-text-primary mb-2">Заголовок наряда</label>
            <input
              value={manualTitle}
              onChange={e => setManualTitle(e.target.value)}
              className="w-full px-4 py-2.5 bg-bg-input text-text-primary rounded-xl border border-border/60
                focus:border-accent focus:ring-1 focus:ring-accent/30 outline-none text-sm"
              data-testid="manual-title"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-text-primary mb-2">Описание</label>
            <textarea
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              rows={4}
              className="w-full px-4 py-3 bg-bg-input text-text-primary rounded-xl border border-border/60
                focus:border-accent focus:ring-1 focus:ring-accent/30 outline-none resize-none text-sm"
              data-testid="manual-description"
            />
          </div>
          <div className="flex gap-3">
            <Button variant="ghost" onClick={() => setManualMode(false)} className="flex-1">
              Назад
            </Button>
            {canCommit && (
              <Button
                onClick={handleManualCreate}
                disabled={creating || !manualTitle.trim()}
                className="flex-1 bg-accent hover:bg-accent/90"
                data-testid="manual-create-submit"
              >
                {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Создать наряд
              </Button>
            )}
          </div>
          {!canCommit && (
            <p className="text-xs text-text-muted">Создать наряд может только менеджер.</p>
          )}
        </div>
      )}

      {/* Prompt input */}
      {!preview && !manualMode && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-text-primary mb-2">
              Опишите задачу в свободной форме
            </label>
            <textarea
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleGenerate()
              }}
              placeholder={installExample ? `Например: ${installExample}` : 'Опишите, что нужно сделать в ЦОДе'}
              rows={4}
              className="w-full px-4 py-3 bg-bg-input text-text-primary rounded-xl border border-border/60 
                focus:border-accent focus:ring-1 focus:ring-accent/30 outline-none resize-none 
                text-sm placeholder:text-text-muted transition-colors"
            />
            <p className="text-xs text-text-muted mt-1">Ctrl+Enter для генерации</p>
          </div>

          <div>
            <label htmlFor="autonomy-level" className="block text-sm font-semibold text-text-primary mb-2">
              Уровень автономии
            </label>
            <select
              id="autonomy-level"
              data-testid="autonomy-level"
              value={autonomyLevel}
              onChange={e => setAutonomyLevel(Number(e.target.value))}
              className="w-full px-4 py-2.5 bg-bg-input text-text-primary rounded-xl border border-border/60
                focus:border-accent focus:ring-1 focus:ring-accent/30 outline-none text-sm"
            >
              <option value={1}>1 — советник</option>
              <option value={2}>2 — черновик</option>
              <option value={3}>3 — к согласованию</option>
            </select>
          </div>

          {/* Example prompts */}
          <div>
            <p className="text-xs font-semibold text-text-muted mb-2">Примеры:</p>
            <div className="flex flex-wrap gap-2">
              {examplePrompts.map((ex, i) => (
                <button
                  key={i}
                  onClick={() => setPrompt(ex)}
                  className="text-xs px-3 py-1.5 rounded-lg border border-border/50 bg-bg-input 
                    hover:bg-bg-hover text-text-secondary hover:text-text-primary transition-colors"
                >
                  {ex}
                </button>
              ))}
            </div>
          </div>

          <Button
            onClick={handleGenerate}
            disabled={loading || !prompt.trim()}
            className="w-full bg-status-blocked hover:bg-status-blocked/90 text-white"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                AI анализирует контекст ЦОДа...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Сгенерировать наряд
              </>
            )}
          </Button>

          {/* Elapsed time + progress bar during inference */}
          {loading && (
            <div className="rounded-xl border border-status-blocked/20 bg-status-blocked/5 p-3 space-y-2">
              <div className="flex items-center justify-between text-xs text-text-muted">
                <span className="flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 text-status-blocked" />
                  Ollama обрабатывает запрос...
                </span>
                <span className="font-mono text-status-blocked font-bold">{elapsed}с</span>
              </div>
              <div className="h-1.5 bg-bg-input rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-status-blocked to-accent rounded-full transition-all"
                  data-testid="generation-progress-bar"
                  style={{
                    // P4-5: реальный eval_count/num_predict из SSE, fallback — по времени
                    width: tokenProgress
                      ? `${Math.min((tokenProgress.done / tokenProgress.total) * 100, 98)}%`
                      : `${Math.min(elapsed / 120 * 100, 95)}%`,
                  }}
                />
              </div>
              <p className="text-[11px] text-text-muted text-center">
                {tokenProgress
                  ? `${tokenProgress.done} / ${tokenProgress.total} токенов`
                  : 'Обычно 30–120 сек в зависимости от размера ЦОДа'}
              </p>
              <Button
                variant="ghost"
                onClick={handleCancelGenerate}
                className="w-full text-text-secondary"
                data-testid="cancel-generation"
              >
                Отменить генерацию
              </Button>
            </div>
          )}
        </div>
      )}

      {/* 503 AI Error block — inline, persistent, with Retry / Manual */}
      {aiError && !preview && !manualMode && (
        <AiErrorBlock
          detail={aiError}
          onRetry={() => { setAiError(null); handleGenerate() }}
          onClose={onClose}
          onManual={() => {
            // P4-4: prefill заголовка из первой строки промпта
            setManualTitle(prompt.trim().split('\n')[0].slice(0, 80))
            setAiError(null)
            setManualMode(true)
          }}
        />
      )}

      {/* Preview */}
      {preview && (
        <div className="space-y-4">
          {/* Title */}
          <div className="rounded-xl border border-status-blocked/20 bg-status-blocked/5 p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-text-muted mb-1">Заголовок наряда:</p>
                <h4 className="font-bold text-text-primary">{preview.title}</h4>
              </div>
              <button
                onClick={() => setPreview(null)}
                className="text-text-muted hover:text-text-primary text-xs underline flex items-center gap-1"
              >
                <RefreshCw className="w-3 h-3" /> Пересоздать
              </button>
            </div>
          </div>

          {/* Autonomy level 1: advisory only */}
          {advisoryOnly && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-accent/10 border border-accent/30" data-testid="advisory-banner">
              <Eye className="w-4 h-4 text-accent shrink-0 mt-0.5" />
              <div className="text-sm text-text-primary">
                AI работает в режиме <strong>советника</strong> (уровень автономии 1).
                Создание наряда из этого превью отключено — используйте план как справку.
              </div>
            </div>
          )}

          {/* Physical conflicts */}
          {blockedCount > 0 && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-status-critical/10 border border-status-critical/30" data-testid="physical-conflicts-banner">
              <ShieldAlert className="w-4 h-4 text-status-critical shrink-0 mt-0.5" />
              <div className="text-sm text-status-critical">
                <strong>{ruPlural(blockedCount, 'шаг', 'шага', 'шагов')}</strong> физически невыполнимы (занятый юнит/порт или объект не найден).
                Исправьте их через выпадающие списки ниже — иначе создание заблокировано.
              </div>
            </div>
          )}

          {/* Unresolved warning */}
          {unresolvedCount > 0 && blockedCount === 0 && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-status-warning/10 border border-status-warning/20">
              <AlertTriangle className="w-4 h-4 text-status-warning shrink-0 mt-0.5" />
              <div className="text-sm text-status-warning">
                <strong>{ruPlural(unresolvedCount, 'шаг', 'шага', 'шагов')}</strong> требуют уточнения (стойка, модель или порт не определены).
                Выберите значения в выпадающих списках ниже.
              </div>
            </div>
          )}

          {/* Serial verification (P1-5) */}
          <SerialVerificationBlock rows={preview.serial_verification} />

          <RackDiffPreview dcCode={dcCode} items={preview.items} />

          {/* AI notes */}
          {preview.unresolved_notes && (
            <div className="p-3 rounded-lg bg-bg-input/50 border border-border/40">
              <p className="text-xs text-text-muted font-semibold mb-1">Заметки AI:</p>
              <p className="text-sm text-text-secondary">{preview.unresolved_notes}</p>
            </div>
          )}

          {/* Items */}
          <div>
            <p className="text-xs font-bold text-text-muted uppercase tracking-widest mb-3">
              Шаги наряда ({preview.items.length})
            </p>
            <div className="space-y-2 max-h-[260px] overflow-y-auto pr-1">
              {preview.items.length === 0 ? (
                <p className="text-sm text-text-muted italic text-center py-4">
                  AI не смог определить шаги. Попробуйте переформулировать запрос.
                </p>
              ) : (
                preview.items.map((item, idx) => (
                  <AiPreviewItemCard
                    key={idx}
                    item={item}
                    index={idx}
                    racks={racks}
                    catalog={catalog}
                    onChange={handleItemChange}
                  />
                ))
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2 border-t border-border/30">
            <Button variant="ghost" onClick={() => setPreview(null)} className="flex-1">
              Назад
            </Button>
            {!advisoryOnly && canCommit && (
              <Button
                onClick={() => {
                  // P2-3: новый ключ идемпотентности на каждый заход в Review Gate
                  idemKeyRef.current = crypto.randomUUID()
                  setShowReviewGate(true)
                }}
                disabled={creating || !reviewReady || recheckPending}
                className="flex-1 bg-accent hover:bg-accent/90"
                data-testid="open-review-gate"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Проверить и создать
              </Button>
            )}
          </div>
          {preview.commit_block_reason && (
            <p className="text-xs text-status-critical pt-2" data-testid="commit-block-reason">
              {preview.commit_block_reason}
            </p>
          )}
          {!canCommit && (
            <p className="text-xs text-text-muted pt-2">Превью доступно. Создать наряд может только менеджер.</p>
          )}
        </div>
      )}

      {/* Human Review Gate (P1-3): блокирует POST до явного подтверждения */}
      {showReviewGate && preview && canCommit && (
        <ReviewGate
          title="Подтверждение AI-наряда"
          operations={buildReviewOperations(preview.items)}
          conflicts={conflicts}
          isLoading={creating}
          onConfirm={handleCreate}
          onCancel={() => setShowReviewGate(false)}
          preview={<RackDiffPreview dcCode={dcCode} items={preview.items} />}
        />
      )}
    </Modal>
  )
}
